import { CONTENT_LANGUAGES, ContentLanguageCode } from '@/shared/constants';
import { Museum } from '@/shared/types';

const contentLanguageDisplayNames = new Intl.DisplayNames(['fr'], { type: 'language' });

/**
 * Libellé affiché pour une langue de contenu (catalogue local, puis Intl en secours).
 */
export const getContentLanguageLabel = (code: string): string => {
  if (CONTENT_LANGUAGES[code]) {
    return CONTENT_LANGUAGES[code];
  }
  try {
    return contentLanguageDisplayNames.of(code) ?? code.toUpperCase();
  } catch {
    return code.toUpperCase();
  }
};

/**
 * Trie les langues pour que 'fr' soit toujours en premier
 * @param languages - Liste des langues à trier
 * @returns Liste triée avec 'fr' en premier
 */
export const sortLanguagesWithFrFirst = <T extends string>(
  languages: T[]
): T[] => {
  if (languages.length === 0) {
    return ['fr' as T];
  }

  const sorted = [...languages];
  const frIndex = sorted.indexOf('fr' as T);

  if (frIndex > 0) {
    sorted.splice(frIndex, 1);
    sorted.unshift('fr' as T);
  }

  return sorted;
};

/**
 * Extrait les codes langue présents dans des champs multilingues Firestore.
 */
export const getContentLanguageKeys = (
  ...records: Array<Record<string, unknown> | undefined | null>
): ContentLanguageCode[] => {
  const keys = new Set<string>();
  records.forEach((record) => {
    if (record && typeof record === 'object') {
      Object.keys(record).forEach((key) => keys.add(key));
    }
  });
  return Array.from(keys) as ContentLanguageCode[];
};

/**
 * Récupère les langues disponibles pour un musée
 * Retourne les langues du musée si disponibles, sinon ['fr', 'en'] par défaut
 */
export const getMuseumLanguages = (museum: Museum | null | undefined): string[] => {
  if (museum?.langs && Array.isArray(museum.langs) && museum.langs.length > 0) {
    return museum.langs;
  }
  // Fallback par défaut
  return ['fr', 'en'];
};

/**
 * Récupère les langues disponibles pour un musée en tant que ContentLanguageCode[]
 */
export const getMuseumLanguagesAsContentCodes = (museum: Museum | null | undefined): ContentLanguageCode[] => {
  return getMuseumLanguages(museum);
};

/**
 * Première valeur non vide d'un champ multilingue (sans priorité fr/en).
 */
export const getMultilingualValue = (
  record: Record<string, string> | undefined | null,
  preferredLang?: string
): string | undefined => {
  if (!record) return undefined;
  if (preferredLang && record[preferredLang]) return record[preferredLang];
  return Object.values(record).find((v) => Boolean(v));
};

/**
 * Valeur pour une langue cible, avec repli sur toute autre traduction disponible.
 */
export const getMultilingualValueForLang = (
  record: Record<string, string> | undefined | null,
  lang: string
): string | undefined => {
  if (!record) return undefined;
  if (record[lang]) return record[lang];
  return getMultilingualValue(record);
};

/**
 * Indique si une valeur est présente dans au moins une langue du record.
 */
export const multilingualRecordHasValue = (
  record: Record<string, string> | undefined | null,
  target: string
): boolean => {
  if (!record) return false;
  return Object.values(record).some((t) => t === target);
};
